import type { ErrorBody } from "@rn/contracts";

/**
 * Cliente da API.
 *
 * Substitui o acesso direto ao Supabase que a v1 fazia do navegador. A
 * diferença que importa não é de estilo: com o front consultando o banco, toda
 * regra de negócio precisava caber numa policy de RLS, e o que não cabia
 * simplesmente não existia.
 */

const BASE = import.meta.env["VITE_API_URL"] ?? "/api";

const ACCESS_KEY = "rn.access";
const REFRESH_KEY = "rn.refresh";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: Partial<ErrorBody>,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Erros de validação por campo, para exibir junto do input certo. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.body?.errors ?? []).map((e) => [e.field, e.message]));
  }
}

// ── Tokens ──────────────────────────────────────────────────────────────────

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/**
 * Renovação em voo compartilhada.
 *
 * Sem isto, várias requisições que recebem 401 ao mesmo tempo disparam vários
 * refresh em paralelo. Como o refresh token é de uso único, o primeiro rotaciona
 * e os demais chegam com um token já revogado — o que a API interpreta como
 * reuso, revoga a família inteira e derruba o usuário. Uma promessa só resolve.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refresh = tokens.refresh;
  if (!refresh) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!response.ok) {
        tokens.clear();
        return false;
      }
      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// ── Requisição ──────────────────────────────────────────────────────────────

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  /** Rotas públicas (login, registro) não anexam token nem tentam renovar. */
  anonymous?: boolean;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    // Arrays viram valores repetidos; a API aceita tanto isso quanto CSV.
    if (Array.isArray(value)) {
      for (const item of value) url.searchParams.append(key, item);
    } else {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function request<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const { method = "GET", body, query, anonymous, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (!anonymous && tokens.access) headers["authorization"] = `Bearer ${tokens.access}`;

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  // 401 numa rota autenticada: renova uma vez e repete. `retried` impede laço
  // infinito quando o refresh também está inválido.
  if (response.status === 401 && !anonymous && !retried) {
    if (await refreshTokens()) return request<T>(path, options, true);
    tokens.clear();
    window.dispatchEvent(new CustomEvent("rn:unauthenticated"));
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = payload as Partial<ErrorBody> | null;
    throw new ApiError(
      error?.message ?? `Erro ${response.status}`,
      response.status,
      error ?? undefined,
    );
  }

  return payload as T;
}

/**
 * Envio de arquivo, separado do `request`.
 *
 * O content-type aqui NÃO pode ser nosso: o navegador precisa escrever o
 * boundary do multipart junto, e um header fixo quebraria a leitura no
 * servidor. O resto do comportamento é igual, inclusive renovar a sessão uma
 * vez no 401.
 */
async function upload<T>(path: string, file: File, field = "file", retried = false): Promise<T> {
  const form = new FormData();
  form.append(field, file, file.name);

  const headers: Record<string, string> = {};
  if (tokens.access) headers["authorization"] = `Bearer ${tokens.access}`;

  const response = await fetch(buildUrl(path), { method: "POST", headers, body: form });

  if (response.status === 401 && !retried) {
    if (await refreshTokens()) return upload<T>(path, file, field, true);
    tokens.clear();
    window.dispatchEvent(new CustomEvent("rn:unauthenticated"));
  }

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = payload as Partial<ErrorBody> | null;
    throw new ApiError(
      error?.message ?? `Erro ${response.status}`,
      response.status,
      error ?? undefined,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  // PUT para substituição total (papéis de um membro, por exemplo); PATCH
  // fica para alteração parcial.
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /** Arquivo para a central de atendimento: foto, áudio, documento. */
  upload,
  /** Para login e registro, que rodam sem sessão. */
  anonymous: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body, anonymous: true }),
};
